import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Minus,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import { useGetRunQuery } from '../../api/runs';
import type { AgentEvalRubricName, AgentTestRunItem } from '../../model/run';
import {
  AGENT_EVAL_RUBRIC_NAMES,
  RUBRIC_LABELS,
  RUNNING_POLL_INTERVAL_MS,
  SCORE_TONE_CLASSES,
  SCORES_POLL_INTERVAL_MS,
  SCORES_STATUS_LABELS,
  formatScore,
  formatTokens,
  formatUsd,
  overallScore,
  runDisplayName,
  runDuration,
  runPromptVersionLabel,
  scoreTone,
} from '../../model/run';
import RunItemDrawer from './RunItemDrawer';
import RunStatusBadge from './RunStatusBadge';

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });

const cellScore = (items: AgentTestRunItem[], scenarioId: number, rubric: AgentEvalRubricName): number | null => {
  const values = items
    .filter((item) => item.agentTestScenarioId === scenarioId)
    .map((item) => item.evaluations?.find((entry) => entry.name === rubric)?.value)
    .filter((value): value is number => typeof value === 'number');
  if (values.length === 0) return null;
  return values.reduce((acc, value) => acc + value, 0) / values.length;
};

interface RunResultsDialogProps {
  runId: number | null;
  scenarioNames: Record<number, string>;
  onRunChange: (runId: number) => void;
  onOpenChange: (open: boolean) => void;
}

const LEGEND: { tone: keyof typeof SCORE_TONE_CLASSES; label: string; dot: string }[] = [
  { tone: 'good', label: 'estable', dot: 'bg-success' },
  { tone: 'warn', label: 'revisar', dot: 'bg-warning' },
  { tone: 'bad', label: 'inestable', dot: 'bg-error' },
];

const RunResultsDialog = ({
  runId,
  scenarioNames,
  onRunChange,
  onOpenChange,
}: RunResultsDialogProps): JSX.Element | null => {
  const [showPrevious, setShowPrevious] = useState(false);
  const [drawerCell, setDrawerCell] = useState<{ scenarioId: number; rubric: AgentEvalRubricName } | null>(null);

  const [pollingInterval, setPollingInterval] = useState(0);
  const { data: run, isLoading, isError } = useGetRunQuery(runId ?? 0, { skip: runId === null, pollingInterval });

  useEffect(() => {
    if (!run || runId === null) {
      setPollingInterval(0);
      return;
    }
    const isRunning = run.status === 'pending' || run.status === 'running';
    const hasPendingScores =
      (run.status === 'completed' || run.status === 'partial' || run.status === 'aborted') &&
      (run.scoresStatus === 'pending' || run.scoresStatus === 'partial');
    setPollingInterval(isRunning ? RUNNING_POLL_INTERVAL_MS : hasPendingScores ? SCORES_POLL_INTERVAL_MS : 0);
  }, [run, runId]);

  useEffect(() => {
    setDrawerCell(null);
  }, [runId]);

  if (runId === null) return null;

  if (isLoading || isError || !run) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Resultados del run</DialogTitle>
          </DialogHeader>
          <p className={cn('m-0 py-6 text-center text-sm', isError ? 'text-error' : 'text-text-muted')}>
            {isError ? 'No se pudieron cargar los resultados del run.' : 'Cargando resultados…'}
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  const items = run.items;
  const name = runDisplayName(run);
  const previousScores = run.previousRun?.scoresSummary ?? null;
  const familyRuns = run.familyRuns ?? [];
  const familyIndex = familyRuns.findIndex((member) => member.id === run.id);
  const goToFamilyRun = (index: number): void => {
    const target = familyRuns[index];
    if (target && target.id !== run.id) onRunChange(target.id);
  };
  const nameOf = (scenarioId: number): string => scenarioNames[scenarioId] ?? `Escenario #${scenarioId}`;
  const drawerItems = drawerCell
    ? items
        .filter((item) => item.agentTestScenarioId === drawerCell.scenarioId)
        .sort((a, b) => a.iterationIndex - b.iterationIndex)
    : [];

  const judgeScore = overallScore(run.scoresSummary);
  const stats: { label: string; value: string }[] = [
    { label: 'Costo total', value: formatUsd(run.totalCostUsd) },
    { label: 'Tokens', value: formatTokens(run.totalUsage) },
    { label: 'Duración', value: runDuration(run) },
    { label: 'Modelo agente', value: run.model ?? '—' },
    { label: 'Prompt', value: runPromptVersionLabel(run) },
    {
      label: 'Score juez',
      value: judgeScore !== null ? formatScore(judgeScore) : SCORES_STATUS_LABELS[run.scoresStatus],
    },
  ];

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
              <div className="flex items-center gap-2">
                <DialogTitle>{name}</DialogTitle>
                <RunStatusBadge status={run.status} />
              </div>
              {run.langfuseRunUrl ? (
                <Button type="button" variant="outline" size="sm" asChild>
                  <a href={run.langfuseRunUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-3.5" aria-hidden />
                    Ver en Langfuse
                  </a>
                </Button>
              ) : null}
            </div>
            <span className="font-mono text-[0.7rem] text-text-muted/70">
              {run.langfuseRunId ?? '—'} · {formatDate(run.createdAt)}
            </span>
            {run.errorMessage ? (
              <p className="m-0 rounded-md border border-warning/30 bg-warning-light px-3 py-1.5 text-xs text-text-default">
                {run.errorMessage}
              </p>
            ) : null}
          </DialogHeader>

          <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-lg border border-border-subtle bg-bg-surface-raised px-4 py-2.5">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-0.5">
                <span className="text-[0.7rem] text-text-muted">{stat.label}</span>
                <span className="text-sm font-semibold text-text-default">{stat.value}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-sm font-semibold text-text-default">Escenario — puntaje del juez</p>
            <div className="flex items-center gap-4">
              {previousScores ? (
                <span className="flex items-center gap-1.5 text-[0.7rem] text-text-muted">
                  <Switch checked={showPrevious} onCheckedChange={setShowPrevious} aria-label="Ver run anterior" />
                  Ver run anterior
                </span>
              ) : null}
              {LEGEND.map((entry) => (
                <span key={entry.tone} className="flex items-center gap-1 text-[0.7rem] text-text-muted">
                  <span className={cn('size-2 rounded-full', entry.dot)} />
                  {entry.label}
                </span>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border-subtle">
            <div className="min-w-[860px]">
              <div className="flex items-center bg-bg-surface-raised">
                <span className="w-[220px] shrink-0 px-3 py-2 text-xs font-medium text-text-muted">Escenario</span>
                {AGENT_EVAL_RUBRIC_NAMES.map((rubric) => (
                  <span key={rubric} className="flex-1 px-2 py-2 text-center text-[0.7rem] font-medium text-text-muted">
                    {RUBRIC_LABELS[rubric]}
                  </span>
                ))}
              </div>
              {run.scenarioIds.map((scenarioId, rowIndex) => (
                <div
                  key={scenarioId}
                  className={cn('flex items-center', rowIndex > 0 && 'border-t border-border-subtle')}
                >
                  <span
                    className="w-[220px] shrink-0 truncate px-3 py-2.5 text-sm text-text-default"
                    title={nameOf(scenarioId)}
                  >
                    {nameOf(scenarioId)}
                  </span>
                  {AGENT_EVAL_RUBRIC_NAMES.map((rubric) => {
                    const score = cellScore(items, scenarioId, rubric);
                    const hasDrawer = items.some((item) => item.agentTestScenarioId === scenarioId);
                    const isSelected = drawerCell?.scenarioId === scenarioId && drawerCell?.rubric === rubric;

                    return (
                      <div key={rubric} className="flex flex-1 flex-col items-center gap-0.5 px-2 py-2">
                        {score !== null ? (
                          <>
                            <button
                              type="button"
                              disabled={!hasDrawer}
                              onClick={() => setDrawerCell({ scenarioId, rubric })}
                              className={cn(
                                'rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap',
                                SCORE_TONE_CLASSES[scoreTone(score)].bg,
                                SCORE_TONE_CLASSES[scoreTone(score)].text,
                                hasDrawer && 'cursor-pointer hover:ring-1 hover:ring-brand-vivid/50',
                                !hasDrawer && 'cursor-default',
                                isSelected && 'ring-2 ring-brand-vivid'
                              )}
                            >
                              {formatScore(score)}
                            </button>
                            {showPrevious && previousScores?.[rubric]?.mean !== undefined ? (
                              <span className="text-[0.65rem] text-text-muted/70">
                                ant. {formatScore(previousScores[rubric]?.mean ?? 0)}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {run.scoresSummary && previousScores ? (
            <div className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-surface-raised px-4 py-3">
              <p className="m-0 text-xs font-semibold text-text-default">
                Comparación vs run anterior ({runDisplayName(run.previousRun!)} ·{' '}
                {formatDate(run.previousRun!.createdAt)})
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {AGENT_EVAL_RUBRIC_NAMES.map((rubric) => {
                  const current = run.scoresSummary?.[rubric]?.mean;
                  const previous = previousScores[rubric]?.mean;
                  if (current === undefined || previous === undefined) return null;
                  const delta = current - previous;
                  const flat = Math.abs(delta) < 0.005;
                  const Icon = flat ? Minus : delta > 0 ? TrendingUp : TrendingDown;
                  const colorClass = flat ? 'text-text-muted' : delta > 0 ? 'text-success' : 'text-error';
                  return (
                    <span key={rubric} className="flex items-center gap-1.5 text-xs">
                      <span className="text-text-muted">{RUBRIC_LABELS[rubric]}</span>
                      <Icon className={cn('size-3.5', colorClass)} aria-hidden />
                      <span className={cn('font-semibold', colorClass)}>
                        {flat ? '0,00' : `${delta > 0 ? '+' : '−'}${formatScore(Math.abs(delta))}`}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-between pt-1">
            {familyRuns.length > 1 && familyIndex >= 0 ? (
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => goToFamilyRun(0)}
                  disabled={familyIndex === 0}
                  aria-label="Primera corrida"
                >
                  <ChevronsLeft className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => goToFamilyRun(familyIndex - 1)}
                  disabled={familyIndex === 0}
                  aria-label="Corrida anterior"
                >
                  <ChevronLeft className="size-4" aria-hidden />
                </Button>
                <span className="px-1.5 text-xs text-text-muted">
                  Corrida {familyIndex + 1} de {familyRuns.length} · {formatDate(run.createdAt)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => goToFamilyRun(familyIndex + 1)}
                  disabled={familyIndex === familyRuns.length - 1}
                  aria-label="Corrida siguiente"
                >
                  <ChevronRight className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => goToFamilyRun(familyRuns.length - 1)}
                  disabled={familyIndex === familyRuns.length - 1}
                  aria-label="Última corrida"
                >
                  <ChevronsRight className="size-4" aria-hidden />
                </Button>
              </div>
            ) : (
              <span className="text-xs text-text-muted">Sin re-runs de esta corrida aún.</span>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {drawerCell ? (
        <RunItemDrawer
          open
          onOpenChange={(open) => {
            if (!open) setDrawerCell(null);
          }}
          scenarioName={nameOf(drawerCell.scenarioId)}
          rubric={drawerCell.rubric}
          item={drawerItems[0] ?? null}
        />
      ) : null}
    </>
  );
};

export default RunResultsDialog;
